import { companyIdentifierChecks } from "@/lib/company-mention-reliability";
import { digitsOnly } from "@/lib/string";
import {
  displayableLegalForm,
  formatSirenFormatted,
  formatSiretFormatted,
  type CompanyDTO,
  type CompanyTag,
  type CompanyWebKeyData,
} from "@shaarp/common";

export type CompanyWebLegalRowId =
  "legalForm" | "capital" | "siren" | "siret" | "naf";

/**
 * One label/value row of the legal identity: an identifier also copies as
 * digits, a NAF code carries its label as `detail`.
 */
export type CompanyWebLegalRow = {
  id: CompanyWebLegalRowId;
  value: string;
  copyValue?: string;
  detail?: string;
};

function identifierRow(
  id: "siren" | "siret",
  value: string | null | undefined,
): CompanyWebLegalRow | null {
  const display =
    id === "siren" ? formatSirenFormatted(value) : formatSiretFormatted(value);
  if (!value || !display) return null;
  return { id, value: display, copyValue: digitsOnly(value) || display };
}

/**
 * The tag's SIREN and SIRET, each only when its check digit is valid: the tag
 * is model output, and a made-up number must not hide the one the web showed.
 */
function checkedTagIdentifiers(
  tag: Pick<CompanyTag, "siren" | "siret"> | null | undefined,
): Pick<CompanyTag, "siren" | "siret"> {
  if (!tag) return {};
  const checks = companyIdentifierChecks({ siren: tag.siren, siret: tag.siret });
  return {
    siren: checks.siren === "valid" ? tag.siren : undefined,
    siret: checks.siret === "valid" ? tag.siret : undefined,
  };
}

/** The rows the base company already shows in its own legal section. */
function rowsInBase(company: CompanyDTO): Set<CompanyWebLegalRowId> {
  const business = company.business;
  const inBase = new Set<CompanyWebLegalRowId>();
  if (!business) return inBase;
  if (displayableLegalForm(business.legalForm)) inBase.add("legalForm");
  if (business.capital != null && business.capital > 0) inBase.add("capital");
  if (business.sirenFormatted || business.siren) inBase.add("siren");
  if (business.siretFormatted || business.siret) inBase.add("siret");
  if (business.nafCode && business.nafLabel) inBase.add("naf");
  return inBase;
}

/**
 * The legal identity of a company as label/value rows, in display order: legal
 * form, capital, SIREN, SIRET, NAF code with its label. The identifiers the tag
 * carries win over the ones the web pages showed, since the profile was looked
 * up by them — when they pass their check digit; otherwise the profile's stand.
 * With `company` (already in base), only the rows its own legal section lacks
 * are kept, as for the key figures.
 */
export function companyWebLegalRows({
  keyData,
  tag,
  company,
}: {
  keyData?: Partial<CompanyWebKeyData> | null;
  tag?: Pick<CompanyTag, "siren" | "siret"> | null;
  company?: CompanyDTO | null;
}): CompanyWebLegalRow[] {
  const nafCode = keyData?.nafCode;
  const legalForm = displayableLegalForm(keyData?.legalForm);
  const tagIds = checkedTagIdentifiers(tag);
  const rows = [
    legalForm ? { id: "legalForm" as const, value: legalForm } : null,
    keyData?.capital
      ? { id: "capital" as const, value: keyData.capital }
      : null,
    identifierRow("siren", tagIds.siren || keyData?.siren),
    identifierRow("siret", tagIds.siret || keyData?.siret),
    nafCode
      ? {
          id: "naf" as const,
          value: nafCode,
          ...(keyData?.nafLabel && { detail: keyData.nafLabel }),
        }
      : null,
  ].filter((row): row is CompanyWebLegalRow => row !== null);

  if (!company) return rows;
  const inBase = rowsInBase(company);
  return rows.filter((row) => !inBase.has(row.id));
}
